import React, { useEffect, useState } from "react";
import {
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ClientLocation } from "./types";
import PaymentRowsEditor, {
  PaymentRow,
  createPaymentRow,
} from "./PaymentRowsEditor";
import { ManualPaymentRowInput } from "./ManualPaymentsModal";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

export type CreditWithRatesSubmit = {
  credit: {
    name: string;
    description?: string;
    currency: string;
    totalAmount: number;
    totalInterest?: number;
    totalWithInterest: number;
    startDate: string;
    disbursementDate?: string;
    locationId?: string | null;
  };
  rates: ManualPaymentRowInput[];
};

type Props = {
  open: boolean;
  loading?: boolean;
  locations?: ClientLocation[];
  onCancel: () => void;
  onSubmit: (payload: CreditWithRatesSubmit) => void;
};

const money = (val: number) =>
  (val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const CreditWithRatesModal: React.FC<Props> = ({
  open,
  loading,
  locations = [],
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const [rows, setRows] = useState<PaymentRow[]>([]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        currency: "EUR",
        startDate: dayjs(),
      });
      setRows([createPaymentRow({ date: dayjs().format("YYYY-MM-DD") })]);
    }
  }, [open, form]);

  const totalWithInterest = Form.useWatch("totalWithInterest", form);
  const rowsTotal = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const leftAfterRows = Math.max(0, (Number(totalWithInterest) || 0) - rowsTotal);

  const handleOk = async () => {
    const values = await form.validateFields();
    const rates: ManualPaymentRowInput[] = rows
      .filter((r) => (Number(r.amount) || 0) > 0)
      .map((r) => ({
        amount: Number(r.amount),
        paymentDate: dayjs(r.date).toISOString(),
        note: r.note || undefined,
      }));

    onSubmit({
      credit: {
        name: values.name,
        description: values.description || undefined,
        currency: values.currency,
        totalAmount: Number(values.totalAmount),
        totalInterest: values.totalInterest
          ? Number(values.totalInterest)
          : undefined,
        totalWithInterest: Number(values.totalWithInterest),
        startDate: values.startDate.toISOString(),
        disbursementDate: values.disbursementDate
          ? values.disbursementDate.toISOString()
          : undefined,
        locationId: values.locationId || null,
      },
      rates,
    });
  };

  return (
    <Modal
      open={open}
      title={t("New credit with installments")}
      width={isMobile ? "100%" : 760}
      onCancel={onCancel}
      onOk={handleOk}
      okButtonProps={{ loading }}
      okText={t("Create credit")}
      className="standardHeader standardFooter"
      destroyOnClose
      styles={
        isMobile
          ? { body: { maxHeight: "70vh", overflowY: "auto" } }
          : undefined
      }
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {t(
          "Set up the credit and enter all its installments in one go. You can still edit the credit or add/edit rates later.",
        )}
      </Text>

      <Form layout="vertical" form={form}>
        <Form.Item
          name="name"
          label={t("Credit name")}
          rules={[{ required: true, message: t("Please enter a name") }]}
        >
          <Input placeholder={t("Example: Office renovation loan")} />
        </Form.Item>
        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea rows={2} placeholder={t("Internal note")} />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="totalAmount"
              label={t("Principal amount")}
              rules={[
                { required: true, message: t("Amount is required") },
                { type: "number", min: 0 },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={50}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label={t("Currency")}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="EUR">EUR</Select.Option>
                <Select.Option value="BAM">BAM</Select.Option>
                <Select.Option value="RSD">RSD</Select.Option>
                <Select.Option value="USD">USD</Select.Option>
                <Select.Option value="CHF">CHF</Select.Option>
                <Select.Option value="GBP">GBP</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label={t("Loan approval date")}
              rules={[{ required: true, message: t("Select approval date") }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="disbursementDate"
              label={t("Disbursement date")}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalInterest"
              label={t("Total interest (amount)")}
              rules={[{ type: "number", min: 0 }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={10}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalWithInterest"
              label={t("Total amount with interest")}
              rules={[
                { required: true, message: t("Total amount is required") },
                { type: "number", min: 0 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const principal = getFieldValue("totalAmount");
                    if (!value || !principal || value >= principal) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          "Total with interest must be greater than or equal to principal amount",
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={50}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
        </Row>
        {locations.length > 0 && (
          <Form.Item name="locationId" label={t("Location (optional)")}>
            <Select allowClear placeholder={t("Select location")}>
              {locations.map((loc) => (
                <Select.Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>

      <Divider />

      <Title level={5} style={{ marginBottom: 4 }}>
        {t("Installments")}
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {t(
          "Enter the first installment, then duplicate it to quickly add next month's payment.",
        )}
      </Text>

      <PaymentRowsEditor rows={rows} onChange={setRows} isMobile={isMobile} />

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Text>
          {t("Total entered")}: <strong>{money(rowsTotal)}</strong>
        </Text>
        <Text type="secondary">
          {t("Left uncovered")}: {money(leftAfterRows)}
        </Text>
      </div>
    </Modal>
  );
};

export default CreditWithRatesModal;
